<template>
  <v-app>
    <v-container>
      <v-row v-if="loading">
        <v-col cols="12" class="text-center">
          <p>Loading requisition details...</p>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col cols="10" offset="1">
          <v-card class="pa-4" id="requisition-content">

            <!-- ===== Header ===== -->
            <v-row>
              <v-col cols="12">
                <h4 class="d-flex justify-center center" style="color:#333;">
                  <strong>ITEM TRANSFER DETAILS</strong>
                </h4>

                <v-row class="justify-space-between">
                  <v-col cols="8">
                    <p><strong>FROM WAREHOUSE:</strong></p>
                    <p class="text-sub-2">{{ fromWarehouse?.name || 'N/A' }}</p>

                    <p class="mt-2"><strong>TO WAREHOUSE:</strong></p>
                    <p class="text-sub-2">{{ toWarehouse?.name || 'N/A' }}</p>
                  </v-col>

                  <v-col cols="4" class="text-right">
                    <h4><strong>TRANSFER #</strong> {{ code }}</h4>
                    <p><strong>TRANSFER DATE:</strong> {{ formatDateView(transfer_date) }}</p>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-col cols="12" class="px-4"><br></v-col>

            <!-- ===== Table ===== -->
            <v-table class="mt-1" width="100%">
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Code</th>
                  <th>Item</th>
                  <th class="text-center">Quantity</th>
                  <th class="text-center">Unit</th>
                  <th class="text-center">Rate</th>
                  <th class="text-center">Amount</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(product, key) in cart" :key="key">
                  <td>{{ key + 1 }}</td>
                  <td>{{ product.product_code }}</td>
                  <td>{{ product.product_name }}</td>
                  <td class="text-center">{{ product.qty }}</td>
                  <td class="text-center">{{ product.unit_name }}</td>
                  <td class="text-center">{{ $numberFormat(product.rate) }}</td>
                  <td class="text-center">{{ $numberFormat(product.amount) }}</td>
                </tr>

                <tr v-if="cart.length === 0">
                  <td colspan="7" class="text-center" style="color:red;">No product details found</td>
                </tr>
              </tbody>
            </v-table>

            <!-- ===== Amount Section ===== -->
            <v-row class="py-10" v-if="total_amount > 0">
              <v-col cols="9">
                <p class="text-right">TOTAL AMOUNT</p>
                <div class="font-weight-bold py-4">
                  <strong>{{ convertNumberToWords(total_amount) }}</strong>
                </div>
              </v-col>

              <v-col cols="3">
                <p class="text-right">{{ $numberFormat(total_amount) }}</p>
              </v-col>
            </v-row>

            <!-- ===== Remarks ===== -->
            <v-row v-if="remarks">
              <v-col cols="12">
                <p><strong>Remarks:</strong> {{ remarks }}</p>
              </v-col>
            </v-row>

            <!-- ===== Buttons ===== -->
            <v-row class="text-center mt-6">
              <v-col cols="12">
                <v-btn color="primary" class="mr-2" @click="printRequisition">
                  <v-icon left>mdi-printer</v-icon> Print
                </v-btn>
                <v-btn color="success" @click="downloadRequisition(requisition_id)">
                  <v-icon left>mdi-file-pdf-box</v-icon> Download PDF
                </v-btn>
              </v-col>
            </v-row>

          </v-card>
        </v-col>
      </v-row>

    </v-container>
  </v-app>
</template>

<script>
import Axios from "axios";
import { prefix } from "@/store/api";
import convertNumberToWords from "@/plugins/numberToWord";
import formatDateView from "@/plugins/dateFormatView";
import { toast } from "vue3-toastify";

export default {
  data() {
    return {
      requisition_id: "",
      code: "",
      transfer_date: "",
      remarks: "",
      total_amount: 0,
      fromWarehouse: {},
      toWarehouse: {},
      cart: [],
      loading: false,
      prefix,
    };
  },

  methods: {
    convertNumberToWords,
    formatDateView,

    /** PDF DOWNLOAD */
    async downloadRequisition(id) {
      try {
        const url = `${prefix}inventory/make-item-transfer-pdf/${id}`;

        const response = await Axios.get(url, { responseType: "blob" });

        if (!response.data) {
          toast.error("Empty PDF received");
          return;
        }

        const blob = new Blob([response.data], { type: "application/pdf" });
        const downloadUrl = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `item-transfer-${this.code || id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(downloadUrl);
      } catch (e) {
        console.error(e);
        toast.error("PDF download failed.");
      }
    },

    /** PRINT WINDOW */
    async printRequisition() {
      let win = window.open("", "PRINT", "width=900,height=650");

      win.document.write(`
        <html>
        <head>
          <title>Requisition</title>
        </head>
        <body>${document.getElementById("requisition-content").innerHTML}</body>
        </html>
      `);

      win.document.close();
      await new Promise((r) => setTimeout(r, 500));
      win.focus();
      win.print();
      win.close();
    },
  },

  mounted() {
    const id = this.$route.query.id;

    if (id) {
      this.loading = true;

      this.$store.dispatch("getInvItemTransferById", id).then((res) => {
        this.loading = false;

        const req = res; // backend sends single object

        if (!req) {
          toast.error("No requisition data found!");
          return;
        }

        this.requisition_id = req.id;
        this.code = req.code;
        this.transfer_date = req.transfer_date;
        this.remarks = req.remarks;

        this.fromWarehouse = req.from_warehouse || {};
        this.toWarehouse = req.to_warehouse || {};

        /** CART ITEMS */
        this.cart = (req.inv_item_transfer_details || []).map((i) => ({
          product_id: i.product_id,
          product_code: i.product?.code ?? "",
          product_name: i.product?.name ?? "",
          qty: i.qty,
          rate: i.rate,
          amount: i.amount,
          unit_name: i.product?.unit?.name ?? "",
        }));

        /** TOTAL */
        this.total_amount = this.cart.reduce(
          (sum, item) => sum + Number(item.amount),
          0
        );
      });
    }
  },
};
</script>

<style scoped>
p {
  color: #444;
}
</style>
